import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';

const Dashboard = () => {
  const { selectedFirmId, company } = useContext(AuthContext);
  const [dailyStats, setDailyStats] = useState(null);
  const [recentTrips, setRecentTrips] = useState([]);
  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);

  const compName = company?.Company_Name || company?.Comapny_Name || 'Hari Ganga Fish Syndicate';
  const compAddress = company?.Address || company?.Comapny_Address || 'Sakhar Koliwada, Alibag, Raigad';
  const compContact = company?.Contact_no || company?.Company_Mobile || '9881914831';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDashboardData();
  }, [selectedFirmId]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [collectionRes, tripsRes, salesRes] = await Promise.all([
        api.get(`/payments/daily-collection?date=${today}`),
        api.get(`/boats/trips?firmId=${selectedFirmId}`),
        api.get(`/sales?firmId=${selectedFirmId}`)
      ]);

      setDailyStats(collectionRes.data);
      setRecentTrips(tripsRes.data.slice(0, 5));
      setRecentSales(salesRes.data.slice(0, 5));
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container container-fluid py-3 px-2 px-md-4">
      {/* FLOATING ACTION BUTTON (FAB) FOR MOBILE */}
      <Link to="/entry/boat-expenses" className="app-fab d-lg-none" title="पलट एन्ट्री / Boat Trip">
        <i className="bi bi-plus-lg fs-2"></i>
      </Link>

      {/* COMPANY DETAILS BANNER ON HOME PAGE */}
      <div className="card border-0 shadow-sm rounded-4 mb-4 text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0369a1 100%)' }}>
        <div className="card-body p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="bg-warning text-dark p-3 rounded-circle shadow-sm d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 50, height: 50 }}>
                <i className="bi bi-building fs-3"></i>
              </div>
              <div>
                <h4 className="fw-extrabold mb-1 text-uppercase text-warning" style={{ fontFamily: 'var(--app-font-heading)', letterSpacing: '0.03em' }}>
                  {compName}
                </h4>
                <div className="d-flex align-items-center gap-3 flex-wrap text-white-50 small">
                  <span>
                    <i className="bi bi-geo-alt-fill text-info me-1"></i>
                    {compAddress}
                  </span>
                  <span>
                    <i className="bi bi-whatsapp text-success me-1"></i>
                    मोबाईल: <strong className="text-white">{compContact}</strong>
                  </span>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <a
                href={`https://wa.me/91${compContact.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-success btn-sm rounded-pill px-3 shadow-sm d-inline-flex align-items-center gap-1"
              >
                <i className="bi bi-whatsapp"></i> व्हॉट्सॲप संपर्क
              </a>
              <button className="btn btn-outline-light btn-sm rounded-pill px-3 shadow-sm" onClick={fetchDashboardData}>
                <i className="bi bi-arrow-clockwise me-1"></i> रिफ्रेश
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* METRIC CARDS GRID */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-xl-3">
          <div className="card border-0 shadow-sm text-white bg-primary bg-gradient h-100 p-2">
            <div className="card-body p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-uppercase small fw-bold opacity-75">आजची विक्री (Sales)</span>
                <div className="bg-white bg-opacity-20 rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                  <i className="bi bi-graph-up fs-5"></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1 fs-4">
                ₹{loading ? '...' : (dailyStats?.salesTotal || 0).toLocaleString('en-IN')}
              </h3>
              <span className="small text-white-50">आज झालेले एकूण बील</span>
            </div>
          </div>
        </div>

        <div className="col-6 col-xl-3">
          <div className="card border-0 shadow-sm text-white bg-success bg-gradient h-100 p-2">
            <div className="card-body p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-uppercase small fw-bold opacity-75">व्यापारी जमा (Collection)</span>
                <div className="bg-white bg-opacity-20 rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                  <i className="bi bi-wallet2 fs-5"></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1 fs-4">
                ₹{loading ? '...' : (dailyStats?.customerReceiptsTotal || 0).toLocaleString('en-IN')}
              </h3>
              <span className="small text-white-50">आज प्राप्त झालेले पेमेंट</span>
            </div>
          </div>
        </div>

        <div className="col-6 col-xl-3">
          <div className="card border-0 shadow-sm text-dark bg-warning bg-gradient h-100 p-2">
            <div className="card-body p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-uppercase small fw-bold opacity-75">विक्रेता दिलेले (Payouts)</span>
                <div className="bg-black bg-opacity-10 rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                  <i className="bi bi-card-checklist fs-5"></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1 fs-4">
                ₹{loading ? '...' : (dailyStats?.vendorPaymentsTotal || 0).toLocaleString('en-IN')}
              </h3>
              <span className="small text-dark-50">सप्लायरला दिलेली रक्कम</span>
            </div>
          </div>
        </div>

        <div className="col-6 col-xl-3">
          <div className="card border-0 shadow-sm text-white bg-info bg-gradient h-100 p-2">
            <div className="card-body p-2 p-md-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-uppercase small fw-bold opacity-75">एकूण पलट (Boat Trips)</span>
                <div className="bg-white bg-opacity-20 rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: 36, height: 36 }}>
                  <i className="bi bi-water fs-5"></i>
                </div>
              </div>
              <h3 className="fw-bold mb-1 fs-4">{loading ? '...' : recentTrips.length}</h3>
              <span className="small text-white-50">नोंदवलेले पलट हिशोब</span>
            </div>
          </div>
        </div>
      </div>

      {/* APP QUICK SHORTCUT GRID (ANDROID TILES) */}
      <div className="mb-4">
        <h6 className="fw-bold text-uppercase text-secondary mb-3 small px-1">
          <i className="bi bi-grid me-1"></i> क्विक कामे / Quick Actions
        </h6>
        <div className="row g-2 g-md-3">
          <div className="col-6 col-sm-4 col-md-3 col-xl-2">
            <Link to="/entry/boat-expenses" className="app-shortcut-tile h-100">
              <div className="app-shortcut-icon bg-primary bg-opacity-10 text-primary">
                <i className="bi bi-water"></i>
              </div>
              <span className="fw-bold small">पलट एन्ट्री</span>
              <span className="text-muted small" style={{ fontSize: '0.7rem' }}>Boat Entry</span>
            </Link>
          </div>

          <div className="col-6 col-sm-4 col-md-3 col-xl-2">
            <Link to="/entry/sales" className="app-shortcut-tile h-100">
              <div className="app-shortcut-icon bg-success bg-opacity-10 text-success">
                <i className="bi bi-receipt"></i>
              </div>
              <span className="fw-bold small">व्यापारी बील</span>
              <span className="text-muted small" style={{ fontSize: '0.7rem' }}>Sale Entry</span>
            </Link>
          </div>

          <div className="col-6 col-sm-4 col-md-3 col-xl-2">
            <Link to="/entry/purchases" className="app-shortcut-tile h-100">
              <div className="app-shortcut-icon bg-danger bg-opacity-10 text-danger">
                <i className="bi bi-cart-plus"></i>
              </div>
              <span className="fw-bold small">खरेदी सामान</span>
              <span className="text-muted small" style={{ fontSize: '0.7rem' }}>Purchase Entry</span>
            </Link>
          </div>

          <div className="col-6 col-sm-4 col-md-3 col-xl-2">
            <Link to="/entry/boxes" className="app-shortcut-tile h-100">
              <div className="app-shortcut-icon bg-warning bg-opacity-10 text-warning">
                <i className="bi bi-box-seam"></i>
              </div>
              <span className="fw-bold small">पेटी ट्रॅकर</span>
              <span className="text-muted small" style={{ fontSize: '0.7rem' }}>Box Tracker</span>
            </Link>
          </div>

          <div className="col-6 col-sm-4 col-md-3 col-xl-2">
            <Link to="/payments/customer-receipts" className="app-shortcut-tile h-100">
              <div className="app-shortcut-icon bg-info bg-opacity-10 text-info">
                <i className="bi bi-wallet2"></i>
              </div>
              <span className="fw-bold small">व्यापारी जमा</span>
              <span className="text-muted small" style={{ fontSize: '0.7rem' }}>Payment Receipt</span>
            </Link>
          </div>

          <div className="col-6 col-sm-4 col-md-3 col-xl-2">
            <Link to="/reports/sales" className="app-shortcut-tile h-100">
              <div className="app-shortcut-icon bg-secondary bg-opacity-10 text-dark">
                <i className="bi bi-bar-chart-line"></i>
              </div>
              <span className="fw-bold small">सर्व रिपोर्ट</span>
              <span className="text-muted small" style={{ fontSize: '0.7rem' }}>All Reports</span>
            </Link>
          </div>
        </div>
      </div>

      {/* RECENT DATA FEEDS */}
      <div className="row g-4">
        {/* RECENT BOAT TRIPS */}
        <div className="col-12 col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-0">
              <h5 className="fw-bold mb-0 text-primary fs-6">
                <i className="bi bi-water me-2"></i>नुकतेच पलट हिशोब (Boat Entry)
              </h5>
              <Link to="/reports/accounts" className="btn btn-link btn-sm text-decoration-none p-0 fw-semibold">
                सर्व पाहा <i className="bi bi-chevron-right"></i>
              </Link>
            </div>

            {/* MOBILE CARD VIEW FOR TRIPS (< 576px) */}
            <div className="d-block d-sm-none px-3 pb-3">
              {recentTrips.length === 0 ? (
                <div className="text-center py-4 text-muted small">No recent trips.</div>
              ) : (
                recentTrips.map((t) => (
                  <div key={t.id} className="p-3 mb-2 rounded-3 bg-light border">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="badge bg-primary">पलट #{t.boat_no}</span>
                      <span className="small text-muted">{t.boat_date}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                      <div>
                        <div className="small text-muted">एकूण विक्री (Sale)</div>
                        <div className="fw-bold text-success">₹{(t.total_sale || 0).toLocaleString('en-IN')}</div>
                      </div>
                      <div className="text-end">
                        <div className="small text-muted">५०% वाटा (50%)</div>
                        <div className="fw-bold text-primary">₹{(t.fifty_percent || 0).toLocaleString('en-IN')}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* DESKTOP TABLE VIEW FOR TRIPS (>= 576px) */}
            <div className="table-responsive d-none d-sm-block">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>पलट क्र (Trip No)</th>
                    <th>तारीख</th>
                    <th>एकूण विक्री (Total Sale)</th>
                    <th>एकूण खर्च (Expenses)</th>
                    <th>५०% वाटा (50% Share)</th>
                    <th>प्रत्येकी वाटा (Per Crew)</th>
                  </tr>
                </thead>
                <tbody>
                  {recentTrips.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-muted">
                        No recent boat trips recorded.
                      </td>
                    </tr>
                  ) : (
                    recentTrips.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <span className="badge bg-primary">पलट #{t.boat_no}</span>
                        </td>
                        <td>{t.boat_date}</td>
                        <td className="fw-bold text-success">₹{(t.total_sale || 0).toLocaleString('en-IN')}</td>
                        <td className="text-danger">₹{(t.boat_total || 0).toLocaleString('en-IN')}</td>
                        <td className="fw-bold text-primary">₹{(t.fifty_percent || 0).toLocaleString('en-IN')}</td>
                        <td>
                          <span className="badge bg-info text-dark">
                            ₹{parseFloat(t.per_employee || 0).toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RECENT SALES */}
        <div className="col-12 col-lg-5">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-0">
              <h5 className="fw-bold mb-0 text-success fs-6">
                <i className="bi bi-receipt me-2"></i>नुकतेच व्यापारी बील (Sale Bills)
              </h5>
              <Link to="/reports/sales" className="btn btn-link btn-sm text-decoration-none p-0 fw-semibold">
                सर्व पाहा <i className="bi bi-chevron-right"></i>
              </Link>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>बील क्र</th>
                    <th>व्यापारी (Customer)</th>
                    <th>तारीख</th>
                    <th className="text-end">एकूण (Grand Total)</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSales.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">
                        No recent sales recorded.
                      </td>
                    </tr>
                  ) : (
                    recentSales.map((s) => (
                      <tr key={s.Id}>
                        <td className="fw-semibold">#{s.saleNo || s.Id}</td>
                        <td className="fw-medium text-primary">{s.customerName || 'नगद (Cash)'}</td>
                        <td className="small text-muted">{s.saleDate}</td>
                        <td className="text-end fw-bold text-success">
                          ₹{(s.GrandTotal || 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
